import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import Tooltip from './Tooltip';
import {
  getSubscriptionPlans,
  getTrialEligibility,
} from '../../actions/subscriptions';
import { getCookie, setCookie } from '../../lib/utils/cookies';
import { formatCurrency } from '../../lib/utils/formatters';
import {
  calculateRewardPoints,
  clearPlusOptIn,
  getPlusOptIn,
  IN_NETWORK_REWARD_RATE,
  setPlusOptIn,
} from '../../lib/utils/plus';

export const PLUS_UPSELL_DISMISSED_COOKIE = 'ck_plus_upsell_dismissed';
const DISMISS_DAYS = 30;
const MONTHLY_PLAN_DURATION_DAYS = 30;

const TOOLTIP_TEXT =
  'Credit Key Plus members earn up to 3% cash back on every purchase, ' +
  'get Pay-in-4 anywhere, and build business credit faster. Cancel anytime.';

/**
 * Order-total-rail upsell card for the terms page. The toggle only
 * records opt-in intent (reversible); the subscription is created at
 * order confirmation, right before the cart confirm request (Confirm.js).
 * Terms.js gates rendering (feature flag, bank on file, NetX,
 * reward-earning order types); the card additionally hides itself unless
 * the company is trial-eligible (no prior subscriptions) and an active
 * monthly plan exists. The X control hides it for 30 days via cookie and
 * clears any opt-in.
 *
 * Props:
 *   cart       – shopping cart entity from Redux (cash back estimate source)
 *   company    – company entity from Redux
 *   rewardRate – reward rate for this order type (rewardRateForOrder)
 */
export default function PlusUpsellCard({
  cart,
  company,
  rewardRate = IN_NETWORK_REWARD_RATE,
}) {
  const dispatch = useDispatch();
  const [dismissed, setDismissed] = useState(
    () => !!getCookie(PLUS_UPSELL_DISMISSED_COOKIE)
  );
  const [plan, setPlan] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [optedIn, setOptedIn] = useState(() => !!getPlusOptIn(cart?.id));

  useEffect(() => {
    if (dismissed || !company?.id) return;

    let isMounted = true;
    Promise.all([
      dispatch(getTrialEligibility(company.id)),
      dispatch(getSubscriptionPlans()),
    ])
      .then(([eligibility, plans]) => {
        if (!isMounted) return;

        const monthlyPlan = plans?.data?.find(
          (p) => p.attributes?.duration === MONTHLY_PLAN_DURATION_DAYS
        );
        setPlan(monthlyPlan?.attributes || null);
        setEligible(eligibility?.eligible_for_trial === true);
      })
      .catch(() => {
        // Stay hidden if eligibility can't be confirmed
      });

    return () => {
      isMounted = false;
    };
  }, [dismissed, company?.id, dispatch]);

  if (dismissed || !eligible || !plan || !cart) return null;

  const points = calculateRewardPoints(cart.grand_total, rewardRate);
  if (points === 0) return null;

  const rewardLabel = `${formatCurrency(points / 100)} (${points.toLocaleString(
    'en-US'
  )} pts)`;
  const trialDays = plan.days_until_first_payment || 30;
  const priceLabel = formatCurrency(plan.price).replace(/\.00$/, '');

  const handleToggle = () => {
    if (optedIn) {
      clearPlusOptIn(cart.id);
      setOptedIn(false);
    } else {
      setPlusOptIn(cart.id, plan.name);
      setOptedIn(true);
    }
  };

  const handleDismiss = () => {
    setCookie(PLUS_UPSELL_DISMISSED_COOKIE, '1', DISMISS_DAYS);
    clearPlusOptIn(cart.id);
    setDismissed(true);
  };

  return (
    <div
      className="mt-3 border border-[#D6E4FF] rounded-xl px-5 py-4 bg-white relative"
      data-testid="plus-upsell-card"
    >
      <button
        type="button"
        aria-label="Dismiss Credit Key Plus offer"
        onClick={handleDismiss}
        className="absolute top-4 right-5 text-secondary-gray hover:text-primary"
      >
        <FontAwesomeIcon icon={['fas', 'times']} />
      </button>

      <div className="flex items-center justify-between gap-4 pr-10">
        <div>
          <div className="flex items-center gap-1.5">
            <h2 className="text-[13px] font-semibold">
              {optedIn ? 'Credit Key Plus Joined' : 'Join Credit Key Plus'}
            </h2>
            <Tooltip
              tooltipText={TOOLTIP_TEXT}
              placement="top"
              triggerClass="px-0.5 hover:cursor-pointer hover:text-primary-blue"
              containerClass="rounded-2xl p-1.5 bg-gray-900 text-white text-xs w-max max-w-xs"
            >
              <button
                type="button"
                aria-label="Learn more about Credit Key Plus"
                className="text-tertiary-gray"
              >
                <FontAwesomeIcon icon={['fal', 'info-circle']} />
              </button>
            </Tooltip>
          </div>
          <p
            className={classNames(
              'flex items-center gap-1.5 text-[11px] font-medium mt-0.5',
              optedIn ? 'text-success' : 'text-primary-blue'
            )}
          >
            {optedIn && (
              <FontAwesomeIcon icon={['fal', 'check']} className="shrink-0" />
            )}
            <span>
              {optedIn ? 'Earning' : 'Earn'} {rewardLabel} back on this order
            </span>
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={optedIn}
          aria-label="Join Credit Key Plus"
          onClick={handleToggle}
          className={classNames(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer',
            optedIn ? 'bg-primary-blue' : 'bg-gray-300'
          )}
        >
          <span
            className={classNames(
              'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
              optedIn ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
      </div>

      <div className="border-t border-[#D6E4FF] mt-3 pt-3 flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-blue">
          {/* Shield glyph from public/ck-mark.svg, filled white */}
          <svg
            viewBox="0 0 35 40"
            className="h-5 w-5"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fillRule="evenodd"
              fill="#FFFFFF"
              d="M17.8365217,19.2595723 L17.8365217,10.1212075 L26.8613665,10.1212075 C26.8613665,15.1682516 22.8206211,19.2595723 17.8365217,19.2595723 L17.8365217,19.2595723 Z M17.8365217,29.8786918 L17.8365217,20.740327 C22.8206211,20.740327 26.8613665,24.8316478 26.8613665,29.8786918 L17.8365217,29.8786918 Z M16.3741615,19.2595723 L7.34931677,19.2595723 C7.34931677,14.2127799 11.3898137,10.1212075 16.3741615,10.1212075 L16.3741615,19.2595723 Z M16.3741615,29.8786918 C11.3898137,29.8786918 7.34931677,25.7873711 7.34931677,20.740327 L16.3741615,20.740327 L16.3741615,29.8786918 Z M31.0954037,8.17881761 L17.1053416,-5.03144656e-05 L3.1152795,8.17881761 C1.18757764,9.30561006 0,11.3883774 0,13.6424654 L0,26.357434 C0,28.611522 1.18757764,30.6942893 3.1152795,31.8213333 L17.1053416,39.9999497 L31.0954037,31.8213333 C33.0231056,30.6942893 34.2106832,28.611522 34.2106832,26.357434 L34.2106832,13.6424654 C34.2106832,11.3883774 33.0231056,9.30561006 31.0954037,8.17881761 L31.0954037,8.17881761 Z"
            />
          </svg>
        </span>
        <p className="text-xs text-secondary-gray leading-relaxed">
          Credit Key Plus &middot; {trialDays}-day free trial, then {priceLabel}
          /mo. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
